import { useEffect, useRef, useState } from 'react';
import { LocalStorageKeys } from 'src/constants/constants';
import { getOrgConfig } from 'src/services/firebase-service';
import { defaultConfig } from 'src/services/pwWebSDKService';

// Extend the Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

const getAppHost = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.host;
};

type Theme = Record<string, string>;

export interface OrgConfig {
  name: string;
  identifier: string;
  id: string;
  baseUrl: string;
  logoUrl: string;
  subOrgId?: string;
  basePath: string;
  enableCohortSelection: boolean;
  favicon: string;
}

export interface AppConfig {
  theme: Theme;
  orgConfig: OrgConfig;
}

interface FirebaseDataConfig {
  data: Record<string, any>;
  appConfig: AppConfig;
}

// Utility to load Google Tag Manager
const loadGTM = (containerId: string) => {
  if (typeof window === 'undefined') return;
  // prevent duplicate load
  if ((window as any).__GTM_LOADED__) return;
  (window as any).__GTM_LOADED__ = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
  // optional noscript fallback
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  const noscript = document.createElement('noscript');
  noscript.appendChild(iframe);
  document.body.appendChild(noscript);
};

let cachedAppConfig: FirebaseDataConfig | null = null;
let appConfigPromise: Promise<FirebaseDataConfig | null> | null = null;

const fetchAppConfig = async (): Promise<FirebaseDataConfig | null> => {
  if (cachedAppConfig) {
    return cachedAppConfig;
  }

  if (appConfigPromise) {
    return appConfigPromise;
  }

  appConfigPromise = (async () => {
    try {
      const orgConfig = await getOrgConfig();
      if (typeof orgConfig === 'string') {
        const parsedOrgConfig = JSON.parse(orgConfig);
        const host = getAppHost();
        const config = parsedOrgConfig?.[host];

        cachedAppConfig = {
          data: parsedOrgConfig,
          appConfig: config,
        };
        return cachedAppConfig;
      }
    } catch (e) {
      console.error('Failed to fetch or parse orgConfig:', e);
    }

    return null;
  })();

  return appConfigPromise;
};

/** Read org config only — safe to call from modals and leaf components. */
const useAppConfig = () => {
  const [appConfig, setAppConfig] = useState<FirebaseDataConfig | null>(cachedAppConfig);

  useEffect(() => {
    let isMounted = true;

    fetchAppConfig().then((config) => {
      if (isMounted) {
        setAppConfig(config);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    appTheme: appConfig?.appConfig?.theme,
    appConfig: appConfig?.appConfig,
    data: appConfig?.data || null,
  };
};

/**
 * Injects global body theme CSS variables. Call once from app root only.
 * Must not be used in modals — unmounting would tear down the global theme.
 */
export const useAppThemeInjection = (appConfig: AppConfig | null | undefined) => {
  const prevClassRef = useRef<string | null>(null);

  useEffect(() => {
    const host = getAppHost();

    if (host.includes('xylem')) {
      document.body.classList.add('xylem');
      loadGTM('GTM-MP8RWZLQ');
    }

    const sarrthiIasFavicon =
      'https://static.pw.live/5b09189f7285894d9130ccd0/284ee581-6091-4bec-8ed6-a180228f4f83.ico';
    if (appConfig?.orgConfig.favicon || sarrthiIasFavicon) {
      const faviconUrl = appConfig?.orgConfig.favicon || sarrthiIasFavicon;
      const element = document.getElementById('favicon') as HTMLLinkElement;
      if (element && element.href !== faviconUrl) {
        element.href = faviconUrl;
      }
    }

    const className = 'dynamic-theme';
    const styleId = `org-theme-style-${className}`;

    const cssVars = Object.entries(appConfig?.theme || {})
      .map(([k, v]) => `  ${k}: ${v};`)
      .join('\n');
    const cssText = `body.${className} {\n${cssVars}\n}`;

    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = cssText;

    if (prevClassRef.current && prevClassRef.current !== className) {
      document.body.classList.remove(prevClassRef.current);
    }
    document.body.classList.add(className);
    prevClassRef.current = className;
  }, [appConfig?.orgConfig.favicon]);
};

export default useAppConfig;

export const updateWebSdkConfig = async (appConfig: AppConfig | null, webSDK: any) => {
  if (!appConfig?.orgConfig || !webSDK) return;

  const { id, baseUrl, subOrgId, identifier, name } = appConfig.orgConfig;
  console.log('[DEBUG] orgConfig destructured', { id, baseUrl, subOrgId, identifier, name });
  // eslint-disable-next-line no-debugger
  // debugger;

  // Persist the resolved orgId so getClientId() returns the correct value.
  // This drives the `client-id` header sent on every API request.
  if (id) {
    try {
      localStorage.setItem(LocalStorageKeys.CLIENT_ID, id);
    } catch (e) {
      console.error('[updateWebSdkConfig] Failed to persist CLIENT_ID', e);
    }
  }

  if (webSDK.organizationId === id) return;
  const updatedConfig = {
    ...defaultConfig,
    ...(subOrgId ? { subOrgId } : {}),
    organizationId: id,
    apiBaseUrl: baseUrl,
  };
  if (name === 'Sarrthi IAS') {
    updatedConfig.contextIdentifier = `TOKEN_CONTEXT_${identifier}`;
  } else {
    updatedConfig.contextIdentifier = 'TOKEN_CONTEXT';
  }

  const finalConfig = { ...defaultConfig, ...updatedConfig };

  webSDK.updateConfig({
    ...defaultConfig,
    ...updatedConfig,
  });

  console.log('[DEBUG] calling webSDK.updateConfig with:', finalConfig);
  console.log('[DEBUG] finalConfig', finalConfig);

  console.log('[INFO] Successfully updated webconfig');
};
